import logger from 'redux-logger';
import ReduxToastr from 'react-redux-toastr';
import createSagaMiddleware from 'redux-saga';
import isPropValid from '@emotion/is-prop-valid';
import { createLogicMiddleware } from 'redux-logic';
import { Provider, useSelector } from 'react-redux';
import { LocalizationProvider } from '@mui/x-date-pickers';
import React, { Fragment, useState, useEffect } from 'react';
import { ThemeProvider, StyleSheetManager } from 'styled-components';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { compose, applyMiddleware, createStore as createReduxStore } from 'redux';
import { ThemeProvider as MUIThemeProvider, THEME_ID } from '@mui/material/styles';
// common
import { makeSelectInitialConfigsLoaded } from './common/selectors';
// root
import mainSaga from './saga';
import arrLogic from './logic';
import reducers from './reducer';
import { Router } from './new-router';
// theme
import { Theme, MUITheme } from './theme';
// helpers
import * as G from './helpers';
// ui
import { RootWrapper } from './ui';
import GlobalStyle from './ui/global';
//////////////////////////////////////////////////

const initialState = {};

let devToolsExtensionComposable = null;

if (
  typeof window !== 'undefined' &&
  process.env.NODE_ENV !== 'production'
) {
  if (window.devToolsExtension) {
    devToolsExtensionComposable = window.devToolsExtension();
  }
}

export function createStore(mainSaga, ...additionalMiddlewares) {
  const logicMiddleware = createLogicMiddleware(arrLogic);
  const sagaMiddleware = createSagaMiddleware();

  if (
    typeof window !== 'undefined' &&
    process.env.NODE_ENV === 'development'
  ) {
    additionalMiddlewares.push(logger);
  }

  const middleWares = [logicMiddleware, sagaMiddleware, ...additionalMiddlewares];

  const composable = [applyMiddleware(...middleWares)];

  if (devToolsExtensionComposable) {
    composable.push(devToolsExtensionComposable);
  }

  const store = createReduxStore(reducers, initialState, compose(...composable));

  if (module.hot) {
    module.hot.accept('./reducer', () => {
      store.replaceReducer(reducers);
    });
  }

  sagaMiddleware.run(mainSaga);

  return store;
}

const store = createStore(mainSaga);

const shouldForwardProp = (propName, target) => {
  if (typeof target === 'string') {
    return isPropValid(propName);
  }

  return true;
};

const ToastrComponent = () => {
  const configsLoaded = useSelector(makeSelectInitialConfigsLoaded());

  const [messagePlacement, setMessagePlacement] = useState(null);

  useEffect(() => {
    setMessagePlacement(G.getToastrMessagePlacement());
  }, [configsLoaded]);

  return (
    <ReduxToastr
      timeOut={10000}
      progressBar={true}
      newestOnTop={false}
      transitionIn='fadeIn'
      key={messagePlacement}
      transitionOut='fadeOut'
      preventDuplicates={true}
      position={messagePlacement}
    />
  );
};

const AppRouter = () => (
  <Provider store={store}>
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ThemeProvider theme={Theme}>
        <MUIThemeProvider theme={{ [THEME_ID]: MUITheme }}>
          <StyleSheetManager shouldForwardProp={shouldForwardProp}>
            <Fragment>
              <RootWrapper>
                <Router />
                <ToastrComponent />
              </RootWrapper>
              <GlobalStyle />
            </Fragment>
          </StyleSheetManager>
        </MUIThemeProvider>
      </ThemeProvider>
    </LocalizationProvider>
  </Provider>
);

export default AppRouter;
